import { useParams, Link } from "react-router-dom";
import { useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { articleContent } from "@/mocks/articles";
import { categories } from "@/mocks/categories";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articleContent[slug as keyof typeof articleContent] : null;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center px-6">
            <h1 className="text-2xl font-heading text-foreground-900">Article Not Found</h1>
            <p className="mt-3 text-sm text-foreground-500">
              The article you are looking for does not exist.
            </p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Back to Home
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main>
        {/* Article Hero */}
        <section className="relative w-full pt-16">
          <div className="relative h-[300px] md:h-[420px] overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-background-50"></div>
          </div>
        </section>

        {/* Article Content */}
        <article className="w-full bg-background-50 px-6 md:px-10 py-10 md:py-16">
          <div className="max-w-3xl mx-auto">
            <div className="mb-8">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer mb-6"
              >
                <i className="ri-arrow-left-line text-sm"></i>
                Back to pandabuy.casa
              </Link>

              <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-label tracking-wider mb-4">
                SEO Guide
              </span>

              <h1 className="text-2xl md:text-4xl font-heading text-foreground-900 leading-tight">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mt-4 text-sm text-foreground-500">
                <span>{article.author}</span>
                <span className="text-foreground-300">·</span>
                <span>{article.date}</span>
                <span className="text-foreground-300">·</span>
                <span>{article.readTime}</span>
              </div>
            </div>

            <div className="prose-custom text-foreground-700 leading-relaxed">
              {article.content.split("\n\n").map((paragraph, i) => (
                <p key={i} className="mb-5 text-sm md:text-base leading-relaxed">
                  {paragraph.trim()}
                </p>
              ))}
            </div>

            {/* Internal links section */}
            <div className="mt-12 pt-8 border-t border-background-200/60">
              <h4 className="text-sm font-label text-foreground-500 mb-4">
                EXPLORE MORE
              </h4>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-xs hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-home-line text-sm"></i>
                  pandabuy.casa Home
                </Link>
                <Link
                  to="/article/shopping-guide"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-xs hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-book-open-line text-sm"></i>
                  Shopping Guide
                </Link>
                {categories.slice(0, 5).map((cat) => (
                  <a
                    key={cat.slug}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-foreground-600 text-xs hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {cat.name}
                    <i className="ri-arrow-right-up-line text-xs"></i>
                  </a>
                ))}
              </div>
            </div>

            {/* CTA Banner */}
            <div className="mt-10 bg-primary-500/10 rounded-2xl p-6 md:p-8 text-center">
              <p className="text-sm text-foreground-700 mb-4">
                Ready to explore curated <strong>pandabuyspreadsheet</strong> collections?
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Pandabuy Sheet
                  <i className="ri-arrow-right-line text-base"></i>
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-foreground-200 text-foreground-700 text-sm hover:border-foreground-400 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Back to Home
                </Link>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />

      {/* Article structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.content.substring(0, 160).replace(/\n/g, " "),
            image: article.image,
            author: {
              "@type": "Organization",
              name: article.author,
            },
            datePublished: "2026-06-28",
            dateModified: "2026-06-29",
            url: `${import.meta.env.VITE_SITE_URL || "https://pandabuy.casa"}/article/${slug}`,
            publisher: {
              "@type": "Organization",
              "@id": `${import.meta.env.VITE_SITE_URL || "https://pandabuy.casa"}/#organization`,
              name: "pandabuy.casa",
            },
            isPartOf: {
              "@type": "WebSite",
              "@id": `${import.meta.env.VITE_SITE_URL || "https://pandabuy.casa"}/#website`,
            },
          }),
        }}
      />

      {/* Breadcrumb structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: import.meta.env.VITE_SITE_URL || "https://pandabuy.casa/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: article.title,
                item: `${import.meta.env.VITE_SITE_URL || "https://pandabuy.casa"}/article/${slug}`,
              },
            ],
          }),
        }}
      />
    </>
  );
}